import { lazy, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { Phone, Shield, Users, Cpu, Award, ArrowRight } from 'lucide-react';
import Seo from '@/components/Seo';
import HeroSlider from '@/components/HeroSlider';
import WhatsAppIcon from '@/components/WhatsAppIcon';
import DeferredSection from '@/components/DeferredSection';
import { usePublicRoutes } from '@/lib/publicRoutes';
import { useCdiAge } from '@/hooks/useCdiAge';
import { interpolateCdiAge } from '../../shared/institutionalAge.js';
import { useSiteLoadingTask } from '@/components/SiteLoadingScreen';

const ConveniosMarquee = lazy(() => import('@/components/ConveniosMarquee'));
const ExamesCarousel = lazy(() => import('@/components/ExamesCarousel'));
const Gallery = lazy(() => import('@/components/Gallery'));
const Depoimentos = lazy(() => import('@/components/Depoimentos'));
const Qualificacoes = lazy(() => import('@/components/Qualificacoes'));




const diferenciais = [
  { icon: Award, titulo: 'Corpo Clínico Especializado', desc: 'Expertise de um corpo clínico altamente qualificado, dedicado a proporcionar os melhores resultados com excelência médica.' },
  { icon: Cpu, titulo: 'Equipamentos de Ponta', desc: 'Tecnologia de última geração com equipamentos avançados para garantir excelência e precisão nos resultados.' },
  { icon: Shield, titulo: 'Segurança Total', desc: 'Protocolos rigorosos garantem a segurança e bem-estar de todos os pacientes durante todo o atendimento.' },
  { icon: Users, titulo: 'Atendimento Humanizado', desc: 'Equipe altamente treinada para proporcionar atendimento acolhedor e personalizado a cada paciente.' },
];

function AnimatedSection({ children, delay = 0, className = '' }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '0px 0px -50px 0px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: inView ? 1 : 0, y: inView ? 0 : 30 }}
      transition={{ duration: 0.5, delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export default function Home() {
  useCdiAge();
  const { path, label } = usePublicRoutes();
  const markHeroReady = useSiteLoadingTask('home-hero');

  return (
    <div>
      <Seo
        title={interpolateCdiAge('CDI Premium — {{idade_cdi}} anos de excelência em diagnósticos por imagem em Goiânia')}
        description={interpolateCdiAge('Clínica de diagnósticos por imagem em Goiânia com {{idade_cdi}} anos de tradição. Ecocardiografia, Doppler, Medicina Nuclear, Ultrassonografia e Radiologia. Agende seu exame!')}
        keywords="CDI Premium, diagnóstico por imagem, Goiânia, ecocardiografia, doppler, medicina nuclear, ultrassonografia, radiologia, exames, cardiologia"
        pageKey="home"
      />
      {/* ── HERO SLIDER ── */}
      <HeroSlider onReady={markHeroReady} />

      {/* ── CONVÊNIOS ── */}
      <DeferredSection Component={ConveniosMarquee} minHeight={180} />

      {/* ── DIFERENCIAIS ── */}
      <section id="sobre" className="py-0 overflow-hidden">
        {/* Header */}
        <div className="bg-cdi-blue px-6 md:px-16 py-12">
          <AnimatedSection>
            <div className="max-w-7xl mx-auto flex items-baseline gap-4">
              <div className="flex-1 h-1 bg-cdi-yellow" />
              <h2 className="text-3xl md:text-5xl font-black text-white uppercase tracking-tight">Diferenciais</h2>
            </div>
            <p className="text-white/60 text-lg font-medium mt-2 text-right max-w-7xl mx-auto">
              Qualidade, tecnologia e humanização em cada atendimento
            </p>
          </AnimatedSection>
        </div>

        {/* Checkboard grid */}
        <div className="grid grid-cols-2 md:grid-cols-4">
          {diferenciais.map((d, i) => {
            const Icon = d.icon;
            const isBlue = i % 2 === 0;
            return (
              <motion.div
                key={d.titulo}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '0px 0px -50px 0px' }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className={`p-8 md:p-10 min-h-[200px] md:min-h-[240px] flex flex-col justify-between border-r border-b ${isBlue ? 'bg-cdi-blue border-cdi-blue/30' : 'bg-cdi-yellow border-cdi-yellow/30'}`}
              >
                <Icon
                  size={36}
                  strokeWidth={1.5}
                  className={isBlue ? 'text-cdi-yellow' : 'text-cdi-blue'}
                />
                <div>
                  <h3
                    className={`font-black text-sm uppercase tracking-wider leading-tight ${isBlue ? 'text-white' : 'text-cdi-blue'}`}
                  >
                    {d.titulo}
                  </h3>
                  <p className={`text-xs mt-1 leading-relaxed font-medium ${isBlue ? 'text-white/60' : 'text-cdi-blue/70'}`}>
                    {d.desc}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* ── NOSSOS EXAMES ── */}
      <section className="py-16 md:py-24 bg-cdi-canvas">
        <AnimatedSection>
          <div className="max-w-7xl mx-auto px-6 mb-12">
            <div className="flex items-baseline gap-4 mb-4">
              <h2 className="text-3xl md:text-5xl font-black text-cdi-blue uppercase tracking-tight">Nossos Exames</h2>
              <div className="flex-1 h-1 bg-cdi-yellow hidden md:block" />
            </div>
            <p className="text-cdi-carbon/60 font-medium">Diagnósticos de precisão com equipamentos de última geração</p>
          </div>
        </AnimatedSection>

        <DeferredSection Component={ExamesCarousel} minHeight={360} />

        <AnimatedSection delay={0.3}>
          <div className="max-w-7xl mx-auto px-6 mt-10 text-center">
            <Link
              to={path('exams')}
              className="inline-flex items-center gap-3 px-8 py-4 bg-cdi-blue text-white font-black text-sm uppercase tracking-wider cdi-shadow hover:translate-x-[-3px] hover:translate-y-[-3px] transition-transform"
            >
              {label('exams')} <ArrowRight size={16} />
            </Link>
          </div>
        </AnimatedSection>
      </section>

      {/* ── GALERIA ── */}
      <DeferredSection Component={Gallery} minHeight={1} />

      {/* ── DEPOIMENTOS ── */}
      <DeferredSection Component={Depoimentos} minHeight={520} />

      {/* ── QUALIFICAÇÕES ── */}
      <DeferredSection Component={Qualificacoes} minHeight={160} />

      {/* ── CTA BANNER ── */}
      <section id="contato" className="bg-cdi-blue py-16 md:py-20 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-32 h-full bg-cdi-yellow/10" />
        <div className="absolute bottom-0 left-0 w-16 h-16 bg-cdi-yellow" />
        <AnimatedSection>
          <div className="max-w-7xl mx-auto px-6 text-center">
            <h2 className="text-3xl md:text-5xl font-black text-white uppercase tracking-tight mb-4">
              Agende Seu Exame Agora
            </h2>
            <p className="text-white/70 text-lg font-medium mb-10 max-w-xl mx-auto">
              Nossa equipe está pronta para atender você com excelência e cuidado.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to={path('schedule')}
                className="flex items-center justify-center gap-3 px-8 py-4 bg-cdi-yellow text-cdi-blue font-black text-sm uppercase tracking-wider cdi-shadow-blue hover:translate-x-[-3px] hover:translate-y-[-3px] transition-transform"
              >
                <WhatsAppIcon size={22} className="text-green-600" />
                Agendar pelo WhatsApp
              </Link>
              <a
                href="tel:6231217272"
                className="flex items-center justify-center gap-3 px-8 py-4 border-4 border-white text-white font-black text-sm uppercase tracking-wider hover:bg-white hover:text-cdi-blue transition-colors"
              >
                <Phone size={18} strokeWidth={2.5} />
                Fale Conosco
              </a>
            </div>
          </div>
        </AnimatedSection>
      </section>
    </div>
  );
}
